import { useEffect, useState } from "react";
import { CalendarDays, Mail, Send, Trash2, Users, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["new", "scheduled", "completed"];
const LABELS = { pending_payment: "Εκκρεμεί πληρωμή", new: "Νέα", scheduled: "Προγραμματισμένη", completed: "Ολοκληρωμένη" };

export const MentorshipAdmin = () => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState({});
  const [filter, setFilter] = useState("all");

  const reload = () => api.get("/admin/mentorship").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line

  const save = async (r, notify = false) => {
    const patch = edit[r.id] || {};
    try {
      await api.put(`/admin/mentorship/${r.id}`, { ...patch, notify });
      toast.success(notify ? "Αποθηκεύτηκε και στάλθηκε email" : "Αποθηκεύτηκε");
      setEdit({ ...edit, [r.id]: undefined });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Διαγραφή αίτησης;")) return;
    await api.delete(`/admin/mentorship/${id}`);
    reload();
  };

  const field = (r, key) => (edit[r.id]?.[key] ?? r[key] ?? "");
  const set = (r, key, value) => setEdit({ ...edit, [r.id]: { ...(edit[r.id] || {}), [key]: value } });
  const shown = (rows || []).filter((r) => filter === "all" || r.kind === filter);
  const groups = [...new Set((rows || []).filter((r) => r.group_id).map((r) => r.group_id))];

  return (
    <div className="mt-10" data-testid="admin-mentorship-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading font-black text-2xl text-[#0B1B3A]">Mentorship</h2>
        <div className="flex gap-2">
          {[["all", "Όλα"], ["one_on_one", "1-1"], ["group", "Group"]].map(([k, l]) => (
            <button key={k} data-testid={`admin-mentorship-filter-${k}`} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-xs font-bold border ${filter === k ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "border-[#E2E8F0] text-[#334155]"}`}>{l}</button>
          ))}
        </div>
      </div>
      {groups.length > 0 && (
        <p className="text-xs text-[#64748B] mt-3" data-testid="admin-mentorship-groups">Ομάδες: {groups.map((g) => `${g} (${rows.filter((r) => r.group_id === g).length})`).join(" · ")}</p>
      )}
      {rows === null ? <p className="text-[#64748B] mt-6">Φόρτωση…</p> : shown.length === 0 ? (
        <div className="card-dark p-12 mt-6 text-center text-[#64748B]" data-testid="admin-mentorship-empty">Δεν υπάρχουν αιτήσεις.</div>
      ) : (
        <div className="mt-6 space-y-4">
          {shown.map((r) => (
            <div key={r.id} className="card-dark p-6" data-testid={`admin-mentorship-row-${r.id}`}>
              <div className="flex flex-wrap items-start gap-4">
                <span className="w-10 h-10 rounded-2xl bg-[#1D4ED8] flex items-center justify-center shrink-0">
                  {r.kind === "group" ? <Users className="w-5 h-5 text-white" /> : <UserRound className="w-5 h-5 text-white" />}
                </span>
                <div className="flex-1 min-w-[240px]">
                  <p className="font-bold text-[#0B1B3A]">{r.user_name} <span className="text-[#64748B] font-normal text-sm">· {r.user_email}</span></p>
                  <p className="text-sm text-[#1E293B] mt-1">{r.kind === "group" ? "Group 4-5" : "1-1"} · <strong>{r.topic}</strong></p>
                  {r.goal && <p className="text-xs text-[#475569] mt-1">Στόχος: {r.goal}</p>}
                  {r.availability && <p className="text-xs text-[#475569]">Διαθεσιμότητα: {r.availability}</p>}
                  {r.message && <p className="text-xs text-[#475569] mt-1 italic">“{r.message}”</p>}
                </div>
                <select data-testid={`admin-mentorship-status-${r.id}`} value={field(r, "status")} onChange={(e) => set(r, "status", e.target.value)} className="input-flat !w-auto text-sm">
                  {STATUSES.map((s) => <option key={s} value={s}>{LABELS[s]}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
                <input data-testid={`admin-mentorship-date-${r.id}`} placeholder="Ημ/νία & ώρα (π.χ. 24/06 18:00)" value={field(r, "scheduled_at")} onChange={(e) => set(r, "scheduled_at", e.target.value)} className="input-flat text-sm" />
                <input data-testid={`admin-mentorship-url-${r.id}`} placeholder="Link συνάντησης (Zoom/Meet)" value={field(r, "meeting_url")} onChange={(e) => set(r, "meeting_url", e.target.value)} className="input-flat text-sm" />
                {r.kind === "group" ? (
                  <input data-testid={`admin-mentorship-group-${r.id}`} placeholder="Ομάδα (π.χ. G1)" value={field(r, "group_id")} onChange={(e) => set(r, "group_id", e.target.value)} className="input-flat text-sm" />
                ) : <span />}
                <input data-testid={`admin-mentorship-notes-${r.id}`} placeholder="Σημειώσεις admin" value={field(r, "admin_notes")} onChange={(e) => set(r, "admin_notes", e.target.value)} className="input-flat text-sm" />
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                <button data-testid={`admin-mentorship-save-${r.id}`} onClick={() => save(r)} className="btn-blue !px-4 !py-2 text-sm"><CalendarDays className="w-4 h-4" /> Αποθήκευση</button>
                <button data-testid={`admin-mentorship-notify-${r.id}`} onClick={() => save(r, true)} className="btn-outline-light !px-4 !py-2 text-sm"><Mail className="w-4 h-4" /> Αποθήκευση + email</button>
                <button data-testid={`admin-mentorship-delete-${r.id}`} onClick={() => remove(r.id)} className="ml-auto p-2 rounded-full text-[#B91C1C] hover:bg-[#FEE2E2]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MentorshipAdmin;
